import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { GeneralDocumentCreateRequest } from 'src/app/_model/request/GeneralDocumentCreateRequest';
import { DocumentService } from 'src/app/_services/document.service';
import { LookupService } from 'src/app/_services/lookup.service';
interface DocumentDialogData {
  id?: number;
  fileName?: string;
  description?: string;
  documentType?: 'file' | 'folder';
  parentFolder?: any;
  filePath?: string;
  isEdit?: boolean;
  orderSeq?: number;
}

@Component({
  selector: 'app-generaldocument-update',
  templateUrl: './generaldocument-update.component.html',
  styleUrls: ['./generaldocument-update.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatRadioModule
  ]
})
export class GeneraldocumentUpdateComponent {

  documentForm: FormGroup;
  selectedFileName: string = null;
  folders: any[] = [];
  filteredFolders: any[] = [];
  folderFilterCtrl = new FormControl('');
  private _onDestroy = new Subject<void>();
  isEditMode: boolean = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<GeneraldocumentUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentDialogData,
    private documentService: DocumentService,
    private toastr: ToastrService
  ) {
    this.isEditMode = data.isEdit;
  }

  ngOnInit() {


    this.initForm();
    this.setupFolderFilter();
    // First load folders, then populate form if in edit mode
    this.loadFolders().subscribe({
      next: () => {
        if (this.isEditMode) {
          this.populateForm();
        }
      }
    });
  }

  private initForm(): void {
    const isFolder = this.data.documentType === 'folder';
    this.documentForm = this.fb.group({
      documentType: [{
        value: this.isEditMode ? this.data.documentType : 'file',
        disabled: this.isEditMode
      }, Validators.required],
      name: ['', Validators.required],
      description: ['', isFolder ? [] : Validators.required], // Make optional for folders
      file: [null],
      parentFolder: [null, isFolder ? [] : Validators.required],
      orderSeq: [0, [
        Validators.required,
        Validators.min(0),
        Validators.max(1000),
        Validators.pattern('^[0-9]*$')
      ]]
    });

    // Set initial validation rules based on initial document type
    this.setValidationRules(this.documentForm.get('documentType').value);

    // Listen for future changes
    this.setupDocumentTypeValidation();
  }


  private populateForm(): void {
    if (!this.data) return;

    let parentFolder = null;
    if (this.data.documentType === 'file' && this.data.parentFolder) {
      parentFolder = this.folders.find(f => f.id === this.data.parentFolder.id);
    } else {
      // Use default option for new files
      parentFolder = this.folders[0];
    }

    this.documentForm.patchValue({
      id: this.data.id,
      documentType: this.data.documentType,
      name: this.data.fileName,
      description: this.data.description,
      parentFolder: parentFolder || null,
      filePath: this.data.filePath,
      orderSeq: this.data.orderSeq || 0,
    });

    if (this.data.filePath) {
      this.selectedFileName = this.data.filePath.split('/').pop();
    }

    this.setValidationRules(this.data.documentType);
  }

  private setupFolderFilter() {
    // Klasör arama filtresini dinle
    this.folderFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterFolders();
      });
  }

  filterFolders() {
    if (!this.folders) {
      return;
    }

    // Arama değerini al
    let search = this.folderFilterCtrl.value;
    if (!search) {
      this.filteredFolders = this.folders.slice();
      return;
    } else {
      search = search.toLowerCase();
    }

    // Filtreleme yap
    this.filteredFolders = this.folders.filter(folder =>
      folder.name.toLowerCase().indexOf(search) > -1
    );
  }

  private loadFolders() {
    return this.documentService.getAllFolders().pipe(
      tap(response => {
        if (response.success) {
          const defaultOption = { id: -1, name: 'Seçiniz', path: 'Seçiniz' };
          this.folders = [defaultOption, ...response.responseData.items];
          this.filteredFolders = this.folders;

          if (!this.isEditMode) {
            this.documentForm.patchValue({
              parentFolder: defaultOption
            });
          }
        }
      })
    );
  }

  private selectedFolderValidator() {
    return (control: FormControl): { [key: string]: any } | null => {
      const folder = control.value;
      // Check if folder is selected and not the default "Seçiniz" option
      if (!folder || !folder.id || folder.id === -1) {
        return { invalidFolder: true };
      }
      return null;
    };
  }

  private setupDocumentTypeValidation(): void {
    this.documentForm.get('documentType').valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(value => {
        this.setValidationRules(value);
      });
  }

  private setValidationRules(documentType: 'file' | 'folder'): void {
    const fileControl = this.documentForm.get('file');
    const parentFolderControl = this.documentForm.get('parentFolder');
    const descriptionControl = this.documentForm.get('description');
    const nameControl = this.documentForm.get('name');
    const orderSeqControl = this.documentForm.get('orderSeq');

    // Clear all validators first
    fileControl.clearValidators();
    parentFolderControl.clearValidators();
    descriptionControl.clearValidators();
    nameControl.clearValidators();
    orderSeqControl.clearValidators();

    // Set common validators
    nameControl.setValidators([Validators.required]);
    orderSeqControl.setValidators([
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
      Validators.pattern('^[0-9]*$')
    ]);

    if (documentType === 'file') {
      // File specific validators
      descriptionControl.setValidators([Validators.required]);
      parentFolderControl.setValidators([
        Validators.required,
        this.selectedFolderValidator()
      ]);

      if (!this.isEditMode) {
        fileControl.setValidators([Validators.required]);
      }
    }

    // Update validity for all controls
    fileControl.updateValueAndValidity();
    parentFolderControl.updateValueAndValidity();
    descriptionControl.updateValueAndValidity();
    nameControl.updateValueAndValidity();
    orderSeqControl.updateValueAndValidity();

    // Update overall form validity
    this.documentForm.updateValueAndValidity();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFileName = file.name;
      this.documentForm.patchValue({
        file: file
      });
    }
  }

  async onSubmit(): Promise<void> {
    if (this.documentForm.valid) {
      const formValue = this.documentForm.value;

      // Base64'e çevrilecek dosya
      let fileBase64 = '';
      if (formValue.file) {
        fileBase64 = await this.convertFileToBase64(formValue.file);
      }

      const request: GeneralDocumentCreateRequest = {
        documentdata: {
          alreadyStored: false,
          fileBase64: fileBase64,
          fileName: formValue.file ? formValue.file.name : '',
          recordId: null,
          willBeDeleted: false
        },
        fileDescription: formValue.description,
        fileName: formValue.name,
        id: this.isEditMode ? this.data.id : null,
        orderSeq: formValue.orderSeq,
        parentId: formValue.parentFolder ? formValue.parentFolder.id : 0,
        type: formValue.documentType
      };

      this.documentService.createDocument(request).subscribe({
        next: (response) => {
          if (response.success) {
            this.dialogRef.close(true);
          } else {
            // Hata durumunu handle et
            console.error('Document creation failed:', response);
          }
        },
        error: (error) => {
          console.error('Error creating document:', error);
        }
      });
    }
  }

  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        // Base64 string'in data:image/jpeg;base64, kısmını kaldır
        const base64Content = base64String.split(',')[1];
        resolve(base64Content);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

}
